import  * as yup from "yup";

const spaceCheckYupValidation = (val?: string) => val ? (!/^\s*$/.test(val)) : false


export const AddCaseSchema = yup.object({
  mos: yup.string().required(),
  submission_source: yup.string().required(),
  user_id: yup.number().required(),
  creator_name: yup.string().required(),
  doctor_firstlastname: yup.string(),
  doctor_fullname: yup.string(),
  patient_firstlastname: yup.string(),
  patient_fullname: yup.string(),
  patient_dob: yup.string(),
  practice_name: yup.string(),
  original_id: yup.number(),
  category: yup.string().required(),

  procedure_attributes: yup.object({
    cpt_code: yup.number()
      .min(10000, 'Invalid Procedure code')
      .max(99999, 'Invalid Procedure code')
      .typeError(`Procedure is required`)
      .required(`Procedure is required`),
  }).required(`Procedure is required`),

  prescription_attributes: yup.object({
    qty: yup.number().min(1, "Qty is required").required("Qty is required"),
    indication: yup.string().required("Indication is required"),
    // ndc: yup.string(),
    // drug_id: yup.number(),
    // days_supply: yup.string(),
    // prescription_attributes_attributes: yup.array().of(
    //   yup.object({
    //     name: yup.string(),
    //     value: yup.string(),
    //   }),
    // ),
    jcode: yup.string().required(),
    // refills_authorized: yup.number(),
    diagnosis_code: yup.array().of(
      yup.string().required(`Diagnosis code is required`)
    ).min(1, `Diagnosis code is required`).required(`Diagnosis code is required`),
    direction: yup
      .string()
      .max(255, "Direction should only be 255 characters long")
      .when({
        is: (val: string) => val && val.length > 0,
        then: (schema) => 
          schema.test("whiteSpaceRemoval", "Direction should not contain only spaces", (val) => spaceCheckYupValidation(val))
      }),
    note: yup
      .string()
      .max(70, "Note should only be 70 characters long")
      .when({
        is: (val: string) => val && val.length > 0,
        then: (schema) => 
          schema
            .test("whiteSpaceRemoval", "Note should not contain only spaces", (val) => spaceCheckYupValidation(val))
      }),
    written_date: yup.string().required('Written date is required'),
  })
  .required(`prescription attributes are required`),

  shipments_attributes: yup.array().of(
    yup.object({
      ship_to: yup.string().required(`Site of Care must be present`),
      ship_to_street: yup.string().when("ship_to", { 
        is: 'other',
        then: (schema) => schema.required(`Ship To Street is required`)
      }),
      ship_to_suite: yup.string(),
      ship_to_city: yup.string().when("ship_to", { 
        is: 'other', 
        then: (schema) => schema.required(`Ship To City is required`)
      }),
      ship_to_state: yup.string().when("ship_to", { 
        is: 'other', 
        then: (schema) => schema.required(`Ship To State is required`)
      }),
      ship_to_zip: yup.string().when("ship_to", { 
        is: 'other', 
        then: (schema) => schema.required(`Ship To Zip is required`)
      }),
    }).required(`shipment attributes are required`),
  ).required(`shipment attributes are required`),

  doctor_attributes: yup.object({
    practice_id: yup.number().required(`Practice is required`),
    global_doctor_id: yup.number().required(`Doctor is required`),
  }),

  patient_attributes: yup.object({
    global_patient_id: yup.number().required(`Patient is required`),
    title: yup.string(),
    firstname: yup
      .string()
      .required("First name is required")
      .max(35, "First name should only be 35 characters long")
      .test("whiteSpaceRemoval", "First name should not contain only spaces", (val) => spaceCheckYupValidation(val)),
    middlename: yup
      .string()
      .max(35, "Middle name should only be 35 characters long")
      .when({
        is: (val: string) => val && val.length > 0,
        then: (schema) => schema
          .test("whiteSpaceRemoval", "Middle name should not contain only spaces", (val) => spaceCheckYupValidation(val))
      }),
    lastname: yup
      .string()
      .required("Last name is required")
      .max(35, "Last name should only be 35 characters long")
      .test("whiteSpaceRemoval", "Last name should not contain only spaces", (val) => spaceCheckYupValidation(val)),
    ssn: yup
      .string()
      .when({
        is: (val: string) => val && val.length > 0,
        then: (schema) => 
          schema
            .min(9, 'SSN should be 9 digits long').max(9, 'SSN should be 9 digits long')
            .test("containAllDigits", "SSN should only contain digits", (val) => val ? (/^\d+$/.test(val)) : false),
      }),
    dob: yup
      .string()
      .required("DOB is required")
      .test(
        "dates-test",
        "Date of Birth should be less than present date",
        (value: string) => {
          const dob = new Date().toISOString().substring(0, 10);
          return dob >= value;
        }
      ),
    gender: yup.string().required("Gender is required"),
    best_time_to_contact: yup.string(),
    reinvestigation: yup.boolean().required(),
    outreach: yup.boolean().required(),
    pcc_outreach: yup.boolean().required(),
    phone_numbers: yup.array().of(
      yup.object({
        phone_type: yup.string().required("Phone type is required"),
        number: yup
          .string()
          .required(`Phone number is required`)
          .min(10, `Phone number invalid`)
          .max(10, `Phone number invalid`)
          .test('range', 'Phone number invalid', (val) => {
            const numberedVal = Number(val)
            if (!isNaN(numberedVal)) {
              if (numberedVal > 2000000000 && numberedVal < 9999999999) return true;
            }
            return false;
          }),
        is_preferred: yup.boolean().default(false).required("Preferred phone is required"),
        is_ok_to_leave_message: yup.boolean().default(false).required("OK to leave message is required"),
        id: yup.number().required().nullable(),
      })
    )
      .min(1, "At least one phone number is required for patient")
      .required(`at least one phone number is required for patient`),

    address: yup.object({
      street: yup
        .string()
        .required(`Street is required`)
        .test("whiteSpaceRemoval", "Street should not contain only spaces", (val) => spaceCheckYupValidation(val)),
      suite: yup
        .string()
        .when({
          is: (val: string) => val && val.length > 0,
          then: (schema) => 
            schema.test("whiteSpaceRemoval", "Suite should not contain only spaces", (val) => spaceCheckYupValidation(val))
        }),
      city: yup
        .string()
        .required(`City is required`)
        .test("whiteSpaceRemoval", "City should not contain only spaces", (val) => spaceCheckYupValidation(val)),
      state: yup.string().required(`State is required`),
      zip: yup
        .string()
        .required(`Zip is required`)
        .max(9, "Zip should not be more than 9 digits")
        .test("containAllDigits", "Zip should only contain digits", (val) => val ? (/^\d+$/.test(val)) : false),
      address_name: yup.string().required(`Address type is required`),
    }).required(`Address for patient information is required`),
  }).required(`Patient details are required`),

  insurances_attributes: yup.array().of(
    yup.object({
      insurance_name: yup
        .string()
        .required(`Insurance Name is required`)
        .test("whiteSpaceRemoval", "Insurance Name should not contain only spaces", (val) => spaceCheckYupValidation(val)),
      // plan_type: yup
      //   .string()
      //   .when({
      //     is: (val: string) => val && val.length > 0,
      //     then: (schema) => schema
      //       .test("whiteSpaceRemoval", "Plan type should not contain only spaces", (val) => spaceCheckYupValidation(val)),
      //   }),
        plan_name: yup
  .string(),
        //.test("whiteSpaceRemoval", "Plan Name should not contain only spaces", (val) => spaceCheckYupValidation(val)),
      relation_to_patient: yup
        .string()
        .required(`Relation to patient is required`),
      relation_firstname: yup
        .string()
        .max(35, "First name should only be 35 characters long")
        .when("relation_to_patient", {
          is: (relation: string) => relation && relation !== "self",
          then: (schema) => schema.required("First name is required"),
        })
        .when({
          is: (val: string) => val && val.length > 0,
          then: (schema) => schema
            .test("whiteSpaceRemoval", "First name should not contain only spaces", (val) => spaceCheckYupValidation(val)),
        }),
      relation_middlename: yup
        .string()
        .max(35, "Middle name should only be 35 characters long")
        .when({
          is: (val: string) => val && val.length > 0,
          then: (schema) => schema
            .test("whiteSpaceRemoval", "Middle name should not contain only spaces", (val) => spaceCheckYupValidation(val)),
        }),
      relation_lastname: yup
        .string()
        .max(35, "Last name should only be 35 characters long")
        .when("relation_to_patient", {
          is: (relation: string) => relation && relation !== "self",
          then: (schema) => schema.required("Last name is required"),
        })
        .when({
          is: (val: string) => val && val.length > 0,
          then: (schema) => schema
            .test("whiteSpaceRemoval", "Last name should not contain only spaces", (val) => spaceCheckYupValidation(val)),
        }),

      policy: yup
        .string()
        .required(`Member id is required`)
        .test("whiteSpaceRemoval", "Member id should not contain only spaces", (val) => spaceCheckYupValidation(val)),
      // group_number: yup
      //   .string()
      //   .max(45, "Group should only be 45 characters long")
      //   .when({
      //     is: (val: string) => val && val.length > 0,
      //     then: (schema) => schema
      //       .test("whiteSpaceRemoval", "Group should not contain only spaces", (val) => spaceCheckYupValidation(val)),
      //   }),
      group_number: yup
  .string()
  .max(45, "Group should only be 45 characters long")
  .test(
    "whiteSpaceRemoval",
    "Group should not contain only spaces",
    (val) => !val || spaceCheckYupValidation(val)
  )
  .when("insurance_name", {
    is: (insurance_name: string) => insurance_name?.toLowerCase() === "other",
    then: (schema) => schema.required("Group is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
      // insurance_number: yup
      //   .string()
      //   .when({
      //     is: (val: string) => val && val.length > 0,
      //     then: (schema) => schema
      //       .min(10, `Insurance phone number invalid`)
      //       .max(10, `Insurance phone number invalid`)
      //       .test('range', 'Insurance phone number invalid', (val) => {
      //         const numberedVal = Number(val)
      //         if (!isNaN(numberedVal)) {
      //           if (numberedVal > 2000000000 && numberedVal < 9999999999) return true;
      //         }
      //         return false;
      //       })
      //   }),
      insurance_number: yup
  .string()
  .test("validPhoneNumber", "Insurance phone number invalid", (val) => {
    if (!val) return true; // Allow empty when optional

    if (!/^\d{10}$/.test(val)) return false;

    const numberedVal = Number(val);
    return numberedVal > 2000000000 && numberedVal < 9999999999;
  })
  .when("insurance_name", {
    is: (insurance_name: string) => insurance_name?.toLowerCase() === "other",
    then: (schema) => schema.required("Phone number is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

      member_id: yup
        .string()
        .max(55, "Rx member id should only be 55 characters long")
        .when({
          is: (val: string) => val && val.length > 0,
          then: (schema) => 
            schema.test("whiteSpaceRemoval", "Rx member id should not contain only spaces", (val) => spaceCheckYupValidation(val))
        }),
      bin: yup
        .string()
        .when({
          is: (val: string) => val && val.length > 0,
          then: (schema) => schema
            .max(6, 'bin should be 6 digits long')
            .test("containAllDigits", "bin should only contain digits", (val) => spaceCheckYupValidation(val)),
        }),
      pcn: yup
        .string()
        .max(45, "PCN should only be 45 characters long")
        .when({
          is: (val: string) => val && val.length > 0,
          then: (schema) => 
            schema.test("whiteSpaceRemoval", "PCN should not contain only spaces", (val) => spaceCheckYupValidation(val))
        }),
      rx_group: yup
        .string()
        .max(45, "Rx group should only be 45 characters long")
        .when({
          is: (val: string) => val && val.length > 0,
          then: (schema) => 
            schema.test("whiteSpaceRemoval", "Rx group should not contain only spaces", (val) => spaceCheckYupValidation(val))
        }),

      is_primary_insurance: yup.boolean().required(),
      insurance_priority: yup.number().required(),
      global_insurance_id: yup.string().required().nullable(),
    }),
  )
  .min(1, "At least one insurance information is required")
  .required("Patient insurance information is required"),
});
